import { Standing } from '@maxpreps/apiserviceclients/apiModels';
import useRequestContext from '@maxpreps/shared/contexts/RequestContext';
import useTeamContext from '@maxpreps/shared/contexts/TeamContext';
import useIsAdmin from '@maxpreps/shared/hooks/useIsAdmin';
import { IChildrenProps } from '@maxpreps/shared/models/utilTypes';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState
} from 'react';
import getCalloutInfo, {
  IScheduleCallout
} from '../../utils/schedule/getCalloutInfo';
import { IClientContest } from '../../utils/schedule/serializeSchedule';
import useIsUserATeamAdmin from '../../utils/useIsUserATeamAdmin';
import getIsPublishedSportSeason from './../../utils/getIsPublishedSportSeason';
import CopyScheduleModal from './CopyScheduleModal';
import ScheduleAlert, { SCHEDULE_ALERT_ID } from './ScheduleAlert';

export interface IScheduleContext {
  standings?: Standing;
  leagueStandings?: Standing;
  contests: IClientContest[];
  setContests: Function;
  selectedContests: any[];
  setSelectedContests: Function;
  showDeleted: boolean;
  setShowDeleted: Function;
  otherLevelTeams: any;
  scheduleCallouts: IScheduleCallout[];
  deleteContests: (contestIds: string[], cb?: Function) => void;
  restoreContests: (contestIds: string[], cb?: Function) => void;
  rebuildGameStory: (contestId: string[], cb?: Function) => void;
  convertToSingleGame: (contestId: string[], cb?: Function) => void;
  convertToSeries: (contestId: string[], cb?: Function) => void;
  copySchedule: () => void;
  setIsCorrectAScoreMode: Function;
  isCorrectAScoreMode: boolean;
  hasCorrectAScoreMode: boolean;
  expandedContestIds: string[];
  setExpandedContestIds: (value: string[]) => void;
  isEligibleForPrint: boolean;
}

const ScheduleContext = createContext<IScheduleContext | undefined>(undefined);

export interface IScheduleContextProviderProps extends IChildrenProps {
  contests: IClientContest[];
  standings: Standing | undefined;
  leagueStandings: Standing | undefined;
}

const ScheduleContextProvider: React.FC<IScheduleContextProviderProps> = ({
  contests: initContests,
  standings,
  leagueStandings,
  children
}) => {
  const isUserATeamAdmin = useIsUserATeamAdmin();
  const teamContext = useTeamContext();
  const { data, teamSeasonPickerData } = teamContext;

  const { appType } = useRequestContext();
  const {
    teamId,
    sportSeasonId,
    canonicalUrl,
    level,
    year,
    season,
    allSeasonId
  } = data;

  const [alertId, setAlertId] = useState(SCHEDULE_ALERT_ID.none);
  const [contextualContests, setContextualContests] = useState<
    any[] | undefined
  >(undefined);
  const [callback, setCallback] = useState(undefined);

  const [showCopyScheduleModal, setShowCopyScheduleModal] = useState(false);
  const hideCopyScheduleModal = () => setShowCopyScheduleModal(false);

  const [contests, _setContests] = useState(initContests);
  const [scheduleCallouts, setScheduleCallouts] = useState<IScheduleCallout[]>(
    []
  );
  const [selectedContests, setSelectedContests] = useState([]);
  const [showDeleted, setShowDeleted] = useState(false);
  const [isCorrectAScoreMode, setIsCorrectAScoreMode] = useState(false);
  const [expandedContestIds, setExpandedContestIds] = useState<string[]>([]);

  const isAdmin = useIsAdmin();

  // **Any added useStates here need to be reset in the useEffect below**

  const teamSeasons =
    teamSeasonPickerData.filter(x => x.isPublished || isAdmin) || [];

  const otherLevelTeams = teamSeasons.filter(
    x => x.level !== level && x.year === year && x.season === season
  );

  const setContests = useCallback(
    (
      updatedSchedule,
      teamId,
      allSeasonId,
      canonicalUrl,
      appType,
      isUserATeamAdmin
    ) => {
      const callouts = isUserATeamAdmin
        ? getCalloutInfo(
            updatedSchedule,
            teamId,
            allSeasonId,
            canonicalUrl,
            appType
          )
        : [];

      _setContests(updatedSchedule);
      setScheduleCallouts(callouts);
    },
    []
  );

  useEffect(() => {
    setSelectedContests([]);
  }, [showDeleted]);

  // When a schedule changes, reset everything to default.
  useEffect(() => {
    const callouts = isUserATeamAdmin
      ? getCalloutInfo(initContests, teamId, allSeasonId, canonicalUrl, appType)
      : [];

    setContests(
      initContests,
      teamId,
      allSeasonId,
      canonicalUrl,
      appType,
      isUserATeamAdmin
    );

    setScheduleCallouts(callouts);
    setAlertId(SCHEDULE_ALERT_ID.none);
    setContextualContests(undefined);
    setCallback(undefined);
    setSelectedContests([]);
    setShowDeleted(false);
    setExpandedContestIds([]);
  }, [
    initContests,
    isUserATeamAdmin,
    allSeasonId,
    canonicalUrl,
    appType,
    setContests,
    teamId
  ]);

  const scheduleContextValue = {
    standings,
    leagueStandings,
    contests,
    setContests,
    selectedContests,
    setSelectedContests,
    showDeleted,
    setShowDeleted,
    otherLevelTeams,
    scheduleCallouts,
    expandedContestIds,
    setExpandedContestIds,
    deleteContests: (contestIds, cb) => {
      if (!contestIds?.length) return;
      setAlertId(
        contestIds.length === 1
          ? SCHEDULE_ALERT_ID.deleteSingle
          : SCHEDULE_ALERT_ID.deleteBulk
      );
      setContextualContests(contestIds);
      setCallback(cb ? () => cb : undefined);
    },
    restoreContests: (contestIds, cb) => {
      if (!contestIds?.length) return;
      setAlertId(
        contestIds.length === 1
          ? SCHEDULE_ALERT_ID.restoreSingle
          : SCHEDULE_ALERT_ID.restoreBulk
      );
      setContextualContests(contestIds);
      setCallback(cb ? () => cb : undefined);
    },
    rebuildGameStory: (contestId, cb) => {
      if (!contestId) return;
      setAlertId(SCHEDULE_ALERT_ID.rebuildGameStory);
      setContextualContests([contestId]);
      setCallback(cb ? () => cb : undefined);
    },
    convertToSingleGame: (contestId, cb) => {
      if (!contestId) return;
      setAlertId(SCHEDULE_ALERT_ID.convertToSingleGame);
      setContextualContests([contestId]);
      setCallback(cb ? () => cb : undefined);
    },
    convertToSeries: (contestId, cb) => {
      if (!contestId) return;
      setAlertId(SCHEDULE_ALERT_ID.convertToSeries);
      setContextualContests([contestId]);
      setCallback(cb ? () => cb : undefined);
    },
    copySchedule: () => {
      setShowCopyScheduleModal(true);
    },
    setIsCorrectAScoreMode,
    isCorrectAScoreMode,
    hasCorrectAScoreMode: contests.some(c => c?.hasResult),
    // cannot print an unpublished sport season
    // the WF page has a bunch of links to other unpublished content!
    isEligibleForPrint: getIsPublishedSportSeason(
      sportSeasonId,
      teamSeasonPickerData
    )
  };

  return (
    <ScheduleContext.Provider value={scheduleContextValue}>
      {children}
      {alertId !== SCHEDULE_ALERT_ID.none && (
        <ScheduleAlert
          alertId={alertId}
          setAlertId={setAlertId}
          contests={contests}
          setContests={setContests}
          contextualContests={contextualContests as any}
          setContextualContests={setContextualContests}
          setSelectedContests={setSelectedContests}
          callback={callback}
        />
      )}

      {showCopyScheduleModal && (
        <CopyScheduleModal
          otherLevelTeams={otherLevelTeams}
          closeModal={hideCopyScheduleModal}
        />
      )}
    </ScheduleContext.Provider>
  );
};

const useScheduleContext = () => {
  const context = useContext(ScheduleContext);
  if (context === undefined) {
    throw new Error('No ScheduleContextProvider');
  }
  return context;
};

export { ScheduleContextProvider, useScheduleContext as default };

